import { inject } from '@angular/core';
import { type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { type Observable, catchError, map, of } from 'rxjs';

import type { Capability } from '../model/capabilities';
import { AUTH_CONFIG } from '../session/auth.config';
import { SessionRefreshService } from '../session/session-refresh.service';
import { SessionService } from '../session/session.service';
import { SUPER_ADMIN_ROLE } from '../session/session.types';
import { CapabilitiesService } from '../capabilities/capabilities.service';

/**
 * The frontend route guards for `@sisoma/auth`, mirroring the backend
 * `CapabilitiesGuard` / `RolesGuard` for UI parity. **Enforcement is always
 * server-side** — these only keep users out of views they cannot use.
 *
 * A not-logged-in user is sent to the shared login (`redirectToLogin`); a
 * logged-in user who lacks access is sent to `AUTH_CONFIG.forbiddenUrl` (a
 * same-origin router path, default `/`).
 *
 * `requireCapabilities` is the one exception: it uses
 * `AUTH_CONFIG.capabilityDeniedUrl` when an app configures one, falling back to
 * `forbiddenUrl`. The other two must not — they gate an entire surface, so
 * their target has to be reachable without passing the gate that just denied,
 * and pointing either at a route behind its own guard is a redirect loop. A
 * capability denial has already cleared that gate, so an app is free to land it
 * somewhere richer (in `partner-area`, inside the shell, where the membership
 * gate can say *why* the capability set is empty).
 *
 * When the *readable* session has merely lapsed (the short-lived `roles_token`
 * expired while the httpOnly refresh token is still valid), the guard tries a
 * single silent refresh before redirecting — so an idle user returning to a
 * partner/admin surface is kept signed in rather than bounced to login.
 */

type GuardResult = boolean | UrlTree;

/**
 * Resolve a guard against the current session. If logged in, decide
 * synchronously (unchanged behaviour). If not, attempt one silent refresh and
 * decide once it settles; a still-invalid session redirects to login. `decide`
 * must not call `inject()` — it runs after the async boundary; capture any
 * dependencies before calling `gate`.
 */
function gate(decide: () => GuardResult): GuardResult | Observable<GuardResult> {
  const session = inject(SessionService);
  const { apiUrl } = inject(AUTH_CONFIG);

  if (session.isLoggedIn()) {
    return decide();
  }
  // No API configured ⇒ the library is inert (tests / unconfigured); there is
  // nothing to refresh against, so fall back to the plain redirect. Mirrors the
  // same guard in `authInterceptor`. Injecting SessionRefreshService is deferred
  // to here so the sync/unconfigured paths don't require an HttpClient.
  if (!apiUrl) {
    session.redirectToLogin();
    return false;
  }
  const refresh = inject(SessionRefreshService);
  return refresh.refresh().pipe(
    catchError(() => of(false)),
    map((loggedIn) => {
      if (!loggedIn) {
        session.redirectToLogin();
        return false;
      }
      return decide();
    }),
  );
}

/**
 * Permit navigation only when the user holds **all** of `caps` in the active
 * partner area. A super admin passes automatically (their capability set is the
 * full catalogue). Usage: `canActivate: [requireCapabilities('partner:invite')]`.
 */
export function requireCapabilities(...caps: Capability[]): CanActivateFn {
  return () => {
    const capabilities = inject(CapabilitiesService);
    const router = inject(Router);
    const { forbiddenUrl, capabilityDeniedUrl } = inject(AUTH_CONFIG);
    const denied = capabilityDeniedUrl ?? forbiddenUrl;

    return gate(() => capabilities.hasAll(...caps) || router.createUrlTree([denied]));
  };
}

/**
 * Gate a partner surface (`partner.halio.ai`) on the user having access to at
 * least one partner area. A super admin passes regardless.
 */
export const requirePartnerAccess: CanActivateFn = () => {
  const session = inject(SessionService);
  const router = inject(Router);
  const { forbiddenUrl } = inject(AUTH_CONFIG);

  return gate(() => {
    if (session.role() === SUPER_ADMIN_ROLE) {
      return true;
    }
    return session.partnerAccess().length > 0 || router.createUrlTree([forbiddenUrl]);
  });
};

/**
 * Permit navigation only when the user's account role is exactly `role`. Use
 * `requireRole(SUPER_ADMIN_ROLE)` to gate the admin surface (`admin.halio.ai`).
 */
export function requireRole(role: string): CanActivateFn {
  return () => {
    const session = inject(SessionService);
    const router = inject(Router);
    const { forbiddenUrl } = inject(AUTH_CONFIG);

    return gate(() => session.role() === role || router.createUrlTree([forbiddenUrl]));
  };
}
